import { TriggerKey } from "@redotech/redo-model/advanced-flow/triggers";
import { ClassifierAction } from "@redotech/redo-model/recover-config";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export const AgeGateStatus = {
  NotVerified: "not_verified",
  VerifiedTrue: "verified_true",
  VerifiedFalse: "verified_false",
} as const;
export type AgeGateStatus = (typeof AgeGateStatus)[keyof typeof AgeGateStatus];

/**
 * @deprecated No longer written. The send path now re-resolves subscription
 * upsell context from the schema instance on the originating automation
 * execution rather than persisting a snapshot on the conversation config.
 * Schema retained for parsing in-flight documents until they age out (30d).
 */
export const subscriptionCartLineSchema = z.object({
  variantGid: z.string(),
  sellingPlanGid: z.string(),
  quantity: z.number().int().positive(),
});
export type SubscriptionCartLineSnapshot = z.infer<
  typeof subscriptionCartLineSchema
>;

export const subscriptionUpsellSnapshotSchema = z.object({
  // Optional + default([]) so legacy snapshots persisted before 2026-05
  // (which only had `checkoutUrl`/`cartId`) still parse. New writers always
  // include lines. TODO: booka66 - drop the default once legacy conversations
  // age out (~30 days from 2026-05-05).
  lines: z.array(subscriptionCartLineSchema).optional().default([]),
  productName: z.string(),
  /**
   * Legacy fields persisted by the pre-2026-05 outreach path, which minted a
   * Storefront cart at trigger time and stored the URL + cart GID directly.
   * Preserved as optional for in-flight conversations; new writers leave them
   * absent. The send path mints fresh from `lines`, falling back to
   * `checkoutUrl` (without discount) only when `lines` is missing.
   * TODO: booka66 - drop these fields and the fallback once in-flight
   * conversations from the legacy path have aged out (~30 days).
   */
  checkoutUrl: z.string().optional(),
  cartId: z.string().optional(),
});
export type SubscriptionUpsellSnapshot = z.infer<
  typeof subscriptionUpsellSnapshotSchema
>;

const marketingConversationTrackingDataSchema_ = z.object({
  /** The recover config associated with this conversation used for generating message content */
  recoverConfigId: zExt.objectId().optional(),
  /** The _id of the most recently selected scenario */
  selectedScenarioId: z.string().optional(),
  /** The classifier action that produced this message */
  classifierAction: createEnum(ClassifierAction).optional(),
  /** @deprecated No longer written as of the no-snapshot migration. */
  checkoutUrl: z.string().optional(),
  /** @deprecated No longer written as of the no-snapshot migration. */
  subscriptionUpsell: subscriptionUpsellSnapshotSchema.nullable().optional(),
  // Automation context for tracking
  automationId: zExt.objectId().optional(),
  automationStepId: z.string().optional(),
  automationExecutionId: z.string().optional(),
  /** Customer event that triggered the automation, when available.*/
  customerEventId: z.string().optional(),
  /** Additional trigger keys that were buffered with the primary trigger. */
  additionalTriggerKeys: z
    .array(z.string().transform((s) => s as TriggerKey))
    .optional(),
  /** Additional customer events that were buffered with the primary trigger. */
  additionalCustomerEventIds: z.array(z.string()).optional(),
  /** Timestamp of when the automation associated with this conversation was triggered */
  automationTriggeredAt: z.date().optional(),
  /** Trigger key for quiet hours and smart sending decisions */
  triggerKey: z
    .string()
    .transform((s) => s as TriggerKey)
    .optional(),
  /** Snapshot of the content upon creation of a draft message, used to show contractor the original message if message was edited and flagged */
  originalContent: z.string().optional(),
  /**
   * A/B test treatment ID - set via $setOnInsert to preserve first assignment
   * Only set AFTER skip checks pass in the workflow, so only "reachable" customers have this
   */
  treatmentId: zExt.objectId().optional(),
  /**
   * Whether this treatment is the control group (no message sent)
   * Passed through the signal so the workflow can check without DB lookup
   */
  isControl: z.boolean().optional(),
  /** Timestamp of when customer most recently entered the recover automation (updated on each entry) */
  enteredAutomationAt: z.date().optional(),
  /** Email of the contractor who last sent or edited the message */
  contractorEmail: z.string().optional(),
  /** Whether this conversation uses iMessage sending (via Mac Mini) */
  isIMessage: z.boolean().optional(),
  /** the associated concierge support conversation id */
  supportConversationId: zExt.objectId().optional(),
  /** the associated concierge support message id */
  supportMessageId: zExt.objectId().optional(),
});
export const marketingConversationTrackingDataSchema: z.ZodType<MarketingConversationTrackingData> =
  marketingConversationTrackingDataSchema_;
export interface MarketingConversationTrackingData
  extends z.infer<typeof marketingConversationTrackingDataSchema_> {}

const marketingConversationConfigSchema_ =
  marketingConversationTrackingDataSchema_.extend({
    _id: zExt.objectId(),
    teamId: zExt.objectId(),
    marketingConversationId: zExt.objectId(),
    ageGateStatus: createEnum(AgeGateStatus)
      .optional()
      .default(AgeGateStatus.NotVerified),
    createdAt: z.date(),
    updatedAt: z.date(),
  });

export const marketingConversationConfigSchema: z.ZodType<MarketingConversationConfig> =
  marketingConversationConfigSchema_;

export interface MarketingConversationConfig
  extends z.infer<typeof marketingConversationConfigSchema_> {}
